import Head from 'next/head'
import { type IPage } from '~/types/generated/contentful'

import { useNavigation } from '../../../context/NavigationProvider'
import { appUrl } from '../../../utils/consts'

type SEOProps = {
  seo?: IPage
}

export default function SEO({ seo }: SEOProps) {
  const { findFullUrlFromId } = useNavigation()

  if (!seo) {
    return <></>
  }

  const {
    title = 'Cynergy Bank',
    seoTitle,
    description = 'Empowering business owners and property entrepreneurs in the UK to fulfil their potential, while continuing to meet the needs of UK savers. We are committed to delivering a comprehensive set of services to meet the evolving needs of our customers and remain a bank focussed on customers, people and relationships.',
    keywords = [],
    ogImage,
    noIndex = false,
    noFollow = false,
    canonicalUrl,
  } = seo.fields

  let robotContent = ''

  if (noFollow) {
    robotContent += 'nofollow'
  }

  if (noIndex) {
    robotContent += (robotContent ? ', ' : '') + 'noindex'
  }

  const fullPath = findFullUrlFromId(seo.sys.id)

  const canonical =
    canonicalUrl ??
    `${appUrl.endsWith('/') ? appUrl.slice(0, -1) : appUrl}${
      fullPath === '/' ? '' : `/${fullPath}`
    }`

  return (
    <Head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      {/* Basic SEO */}
      <title>{seoTitle ?? title}</title>
      <meta name="description" content={description || ''} />
      <meta name="keywords" content={keywords.join(', ')} />
      {robotContent && <meta name="robots" content={robotContent} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seoTitle ?? title} />
      <meta property="og:description" content={description || ''} />
      <meta property="og:image" content={ogImage?.fields.file.url || ''} />
      <meta property="og:site_name" content="https://www.cynergybank.co.uk/" />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={seoTitle ?? title} />
      <meta name="twitter:description" content={description || ''} />
      <meta name="twitter:image" content={ogImage?.fields.file.url || ''} />
      <meta name="twitter:site" content="@CynergyBank" />
      <meta name="twitter:creator" content="@CynergyBank" />
    </Head>
  )
}
